import { useState } from 'react';

export default function Hero() {
  const [useVideo, setUseVideo] = useState(true);

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Video/Image Background Toggle */}
      {useVideo ? (
        // Video Background
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover object-center"
            poster="https://public.readdy.ai/ai/img_res/a0efa889d10b89ca3510bd112b149a9d.jpg"
          >
            <source
              src="https://cdn.coverr.co/videos/coverr-sports-action-montage-8456/1080p.mp4"
              type="video/mp4"
            />
            <source
              src="https://cdn.coverr.co/videos/coverr-basketball-game-highlights-3421/1080p.mp4"
              type="video/mp4"
            />
            {/* Fallback to image if video fails */}
            <img
              src="https://readdy.ai/api/search-image?query=dynamic%20collage%20of%20multiple%20professional%20athletes%20in%20action%20including%20football%20basketball%20baseball%20soccer%20players%20competing%20with%20intense%20energy%20and%20determination%20on%20sports%20fields%20and%20courts%20with%20dramatic%20lighting%20and%20motion%20blur%20creating%20powerful%20athletic%20atmosphere&width=1920&height=1080&seq=hero-sports-bg-01&orientation=landscape"
              alt="Professional Athletes in Action"
              className="w-full h-full object-cover object-top"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>
      ) : (
        // Static Image Background
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dynamic%20collage%20of%20multiple%20professional%20athletes%20in%20action%20including%20football%20basketball%20baseball%20soccer%20players%20competing%20with%20intense%20energy%20and%20determination%20on%20sports%20fields%20and%20courts%20with%20dramatic%20lighting%20and%20motion%20blur%20creating%20powerful%20athletic%20atmosphere&width=1920&height=1080&seq=hero-sports-bg-01&orientation=landscape"
            alt="Professional Athletes in Action"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>
      )}

      {/* Background Toggle Button */}
      <button
        onClick={() => setUseVideo(!useVideo)}
        className="absolute top-24 right-6 z-20 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white text-sm font-semibold hover:bg-white/20 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
        title={useVideo ? 'Switch to Image' : 'Switch to Video'}
      >
        <i className={`${useVideo ? 'ri-image-line' : 'ri-video-line'} text-lg`}></i>
        <span>{useVideo ? 'Image' : 'Video'}</span>
      </button>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center max-w-5xl mx-auto">
          {/* Attention-Grabbing Badge */}
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500/20 border border-teal-400/30 rounded-full mb-8 animate-bounce backdrop-blur-sm">
            <i className="ri-flashlight-fill text-teal-400 text-xl"></i>
            <span className="text-teal-300 font-semibold text-sm">Save 20+ Hours Per Week on Equipment Management</span>
          </div>

          {/* Powerful Headline */}
          <h1 className="text-6xl lg:text-8xl font-black text-white mb-6 leading-tight drop-shadow-2xl">
            Stop Wasting Time.
            <br />
            <span className="bg-gradient-to-r from-teal-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
              Start Winning.
            </span>
          </h1>
          
          <p className="text-2xl text-gray-100 mb-6 font-medium drop-shadow-lg">
            The equipment management platform that athletic directors actually love.
          </p>

          <p className="text-lg text-gray-200 mb-12 max-w-3xl mx-auto drop-shadow-md">
            Order in seconds. Track in real-time. Get back to coaching. TeamPro handles the equipment chaos so you can focus on what matters—your team's success.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-6 mb-16">
            <button 
              onClick={() => {
                const element = document.getElementById('equipment-store');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group px-10 py-5 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-lg font-bold rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all shadow-2xl shadow-teal-500/50 hover:shadow-teal-500/70 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-3">
                Start Free Trial
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </span>
            </button>
            <button 
              onClick={() => {
                const element = document.getElementById('demo');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-10 py-5 bg-white/10 backdrop-blur-sm text-white text-lg font-bold rounded-xl hover:bg-white/20 transition-all border border-white/20 hover:border-white/40 whitespace-nowrap cursor-pointer"
            >
              Watch Demo
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-12 text-white/90 mb-16">
            <div className="flex items-center gap-3 backdrop-blur-sm bg-white/5 px-6 py-4 rounded-2xl">
              <i className="ri-team-fill text-3xl text-teal-400"></i>
              <div className="text-left">
                <div className="text-2xl font-bold text-white">50+</div>
                <div className="text-sm">Teams Trust Us</div>
              </div>
            </div>
            <div className="flex items-center gap-3 backdrop-blur-sm bg-white/5 px-6 py-4 rounded-2xl">
              <i className="ri-shopping-bag-3-fill text-3xl text-pink-400"></i>
              <div className="text-left">
                <div className="text-2xl font-bold text-white">45+</div>
                <div className="text-sm">Premium Products</div>
              </div>
            </div>
            <div className="flex items-center gap-3 backdrop-blur-sm bg-white/5 px-6 py-4 rounded-2xl">
              <i className="ri-time-fill text-3xl text-orange-400"></i>
              <div className="text-left">
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-sm">Order Anytime</div>
              </div>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-white/20 transition-all hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-flashlight-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Lightning Fast</h3>
              <p className="text-gray-200">Order equipment in under 60 seconds. No paperwork, no hassle.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-white/20 transition-all hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-dashboard-3-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Total Visibility</h3>
              <p className="text-gray-200">Track every piece of equipment in one beautiful dashboard.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-white/20 transition-all hover:scale-105 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <i className="ri-truck-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Direct Delivery</h3>
              <p className="text-gray-200">Fast shipping, tracked delivery, guaranteed satisfaction.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
